import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  Progress,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Image,
  Stack
} from '@chakra-ui/react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { eventService, voteService } from '../../services/eventService';
import { useDataStore } from '../../contexts/DataStoreContext';
import { useAuth } from '../../hooks/useAuth';
import { PrizeShowcase } from './PrizeShowcase';

export const EventView = () => {
  const { eventId } = useParams(); // This could be either an ID or a slug now
  const navigate = useNavigate();
  const location = useLocation();
  const dataStore = useDataStore();
  const [event, setEvent] = useState(null);
  const [resolvedEventId, setResolvedEventId] = useState(null);
  const [voteStats, setVoteStats] = useState({});
  const [userVotes, setUserVotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingVote, setPendingVote] = useState(null);
  const [isSubmittingVote, setIsSubmittingVote] = useState(false);
  const [voteDebounceMap, setVoteDebounceMap] = useState(new Map());
  const { user, loading: authLoading } = useAuth();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();


  // Set up real-time subscriptions
  useEffect(() => {
    const loadEvent = async () => {
      setLoading(true);
      setError('');

      try {
        let resolvedEventId = null;

        if (!eventId) {
          navigate('/404', { replace: true });
          return;
        }

        // Wait for DataStore to load before trying to resolve the event
        if (dataStore.loading.events || dataStore.loading.fights) {
          return; // Wait for DataStore to finish loading
        }

        // Try to get event by eventId first if it's longer than 15 characters (Firebase IDs are longer and contain specific characters)
        const searchByEventIdFirst = eventId.length > 15;
        if (searchByEventIdFirst) {
          const eventById = dataStore.getEvent(eventId);
          if (eventById) {
            resolvedEventId = eventById.id;
          }
        }

        // If not found by eventId, or we skipped searching it first, try by slug
        if (!resolvedEventId) {
          let eventBySlug = await eventService.getEventBySlug(eventId);
          if (eventBySlug) {
            resolvedEventId = eventBySlug.id;
          } else if (!searchByEventIdFirst) {
            // fallback to eventID if it wasn't found by slug
            const eventById = dataStore.getEvent(eventId);
            if (eventById) {
              resolvedEventId = eventById.id;
            }
          }
        }

        // If still not found, redirect to 404 and return
        if (!resolvedEventId) {
          navigate('/404', { replace: true });
          return;
        }

        setResolvedEventId(resolvedEventId);

        // Get event data from DataStore (real-time via context)
        const eventData = dataStore.getEnrichedEvent(resolvedEventId);
        if (eventData) {
          setEvent(eventData);
          setError('');
          setLoading(false);
        } else {
          // If we have a resolved ID but no event data, something is wrong
          navigate('/404', { replace: true });
          return;
        }
      } catch (err) {
        setError(err.message || 'Failed to load event');
        setLoading(false);
      }
    };

    loadEvent();

    // Cleanup function - no longer needed since we're using DataStore
    return () => { };
  }, [eventId, navigate, dataStore, dataStore.loading]);

  // Watch for DataStore changes and update event data
  useEffect(() => {
    if (resolvedEventId) {
      const eventData = dataStore.getEnrichedEvent(resolvedEventId);
      if (eventData) {
        setEvent(eventData);
        setLoading(false);
      } else if (!dataStore.loading.events && !dataStore.loading.fights) {
        // If DataStore has finished loading and event doesn't exist, redirect to 404
        navigate('/404', { replace: true });
      }
    }
  }, [resolvedEventId, dataStore.events, dataStore.fights, dataStore.fighters, dataStore.loading, navigate]);

  // Watch for user votes changes in DataStore
  useEffect(() => {
    if (!resolvedEventId || !user) {
      setUserVotes({});
      return;
    }

    // Get user votes from DataStore
    const userVotesData = {};
    const allVotes = Array.from(dataStore.votes.values()).filter(vote =>
      vote.eventId === resolvedEventId && vote.userId === user.uid
    );

    allVotes.forEach(vote => {
      const key = vote.round
        ? `${vote.fightId}_round_${vote.round}`
        : `${vote.fightId}_fight`;
      userVotesData[key] = vote.fighterId;
    });

    setUserVotes(userVotesData);
  }, [resolvedEventId, user, dataStore.votes]);

  // Calculate vote statistics from DataStore
  useEffect(() => {
    if (!event || !event.fights || !user) {
      setVoteStats({});
      return;
    }

    const newVoteStats = {};

    event.fights.forEach(fight => {
      // Calculate fight winner vote stats
      const fightStats = dataStore.getVotesForFight(event.id, fight.id);
      newVoteStats[`${fight.id}_fight`] = fightStats;

      // Calculate round vote stats if fight is active
      if (fight.status === 'active' && fight.currentRound) {
        const roundStats = dataStore.getVotesForFight(event.id, fight.id, fight.currentRound);
        newVoteStats[`${fight.id}_round_${fight.currentRound}`] = roundStats;
      }
    });

    setVoteStats(newVoteStats);
  }, [event, user, dataStore.votes, dataStore]);

  // Show confirmation dialog before voting
  const handleVoteClick = (fight, fighterId, type, round) => {
    if (!user?.uid || !event?.id || !fight?.id) return;

    // Check if user has already voted for this fight/round
    const voteKey = round ? `${fight.id}_round_${round}` : `${fight.id}_fight`;
    if (userVotes[voteKey]) {
      toast({
        title: 'Already voted',
        description: 'You have already voted for this fight/round',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if vote is currently being submitted
    if (isSubmittingVote) {
      toast({
        title: 'Please wait',
        description: 'A vote is currently being submitted',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // Debounce rapid clicks - prevent multiple clicks within 1 second
    const debounceKey = `${fight.id}_${fighterId}_${type}_${round || 'fight'}`;
    const now = Date.now();
    const lastClickTime = voteDebounceMap.get(debounceKey) || 0;

    if (now - lastClickTime < 1000) {
      toast({
        title: 'Slow down',
        description: 'Please wait a moment before voting again',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // Update debounce map
    setVoteDebounceMap(prev => new Map(prev.set(debounceKey, now)));

    // Find the fighter name for confirmation
    const fighter = fight.fighter1?.id === fighterId ? fight.fighter1 : fight.fighter2;
    const voteTypeText = round ? `Round ${round} winner` : 'Fight winner';

    setPendingVote({
      fight,
      fighterId,
      type,
      round,
      fighterName: fighter?.name || 'Unknown Fighter',
      voteTypeText
    });
    onOpen();
  };

  // Actually cast the vote after confirmation
  const confirmVote = async () => {
    if (!pendingVote || isSubmittingVote) return;

    const { fight, fighterId, type, round } = pendingVote;
    const voteKey = round ? `${fight.id}_round_${round}` : `${fight.id}_fight`;

    // Set submitting state to disable all vote buttons
    setIsSubmittingVote(true);

    // Optimistic UI update - store the fighterId they voted for
    setUserVotes(prev => ({ ...prev, [voteKey]: fighterId }));

    try {
      await voteService.castVote({
        eventId: event.id,
        fightId: fight.id,
        userId: user.uid,
        fighterId,
        type,
        round
      });

      toast({
        title: 'Vote cast successfully!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      // Revert optimistic update on error
      setUserVotes(prev => {
        const newVotes = { ...prev };
        delete newVotes[voteKey];
        return newVotes;
      });

      toast({
        title: 'Failed to cast vote',
        description: error?.message || 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      // Always clear submitting state
      setIsSubmittingVote(false);
    }

    // Close modal and clear pending vote
    onClose();
    setPendingVote(null);
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'blue';
      case 'active': return 'green';
      case 'completed': return 'gray';
      default: return 'gray';
    }
  };

  const renderVoteStats = (stats, voteKey, fight) => {
    const hasUserVoted = userVotes[voteKey];
    const isFightCompleted = fight?.status === 'completed' || fight?.winnerId;

    if (!stats || !Array.isArray(stats)) {
      return null;
    }

    const totalVotes = stats.reduce((sum, stat) => sum + stat.votes, 0);

    if (totalVotes === 0) {
      return null;
    }

    // Show results if user has voted OR if fight is completed
    if (!hasUserVoted && !isFightCompleted) {
      return (
        <Text fontSize="sm" color="gray.400" textAlign="center" fontStyle="italic">
          Vote to see results
        </Text>
      );
    }

    return (
      <VStack spacing={2} align="stretch">
        {stats.map(stat => (
          <Box key={stat.fighterId}>
            <HStack justify="space-between" mb={1}>
              <Text fontSize="sm" fontWeight="medium">{stat.fighterName}</Text>
              <Text fontSize="sm" color="gray.300">
                {stat.percentage.toFixed(0)}%
              </Text>
            </HStack>
            <Progress
              value={stat.percentage}
              colorScheme={stat.percentage > 50 ? "green" : "blue"}
              size="sm"
              borderRadius="md"
            />
          </Box>
        ))}
        <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
          Updates in real-time
        </Text>
      </VStack>
    );
  };

  if (loading || authLoading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" />
        <Text mt={4}>Loading event...</Text>
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Box maxW="md" mx="auto" mt={10}>
        <Alert status="error">
          <AlertIcon />
          {error || 'Event not found'}
        </Alert>
      </Box>
    );
  }

  // Handle sign in navigation with return URL
  const handleSignIn = () => {
    const returnUrl = encodeURIComponent(location.pathname);
    navigate(`/auth?returnUrl=${returnUrl}`);
  };

  // Anonymous user view - show event info but prompt for sign in to vote
  if (!user) {
    return (
      <Box maxW="4xl" mx="auto" p={{ base: 2, md: 4 }}>
        {/* Event Header */}
        <Card mb={6}>
          {/* Cover Image */}
          {event?.coverImage && (
            <Box>
              <Image
                src={event.coverImage}
                alt={`${event.name} cover image`}
                width="100%"
                height={{ base: "200px", md: "300px" }}
                objectFit="cover"
                borderTopRadius="lg"
              />
            </Box>
          )}
          <CardHeader pb={{ base: 3, md: 6 }}>
            <VStack align="start" spacing={2}>
              <Heading size={{ base: "md", md: "lg" }}>{event?.name || 'Event'}</Heading>
              <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>
                {event?.location || 'Location TBD'}
              </Text>
              <Text color="gray.400" fontSize={{ base: "xs", md: "sm" }}>
                {event?.date instanceof Date
                  ? event.date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })
                  : 'Date TBD'
                }
              </Text>
              {event?.description && (
                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                  {event.description}
                </Text>
              )}
            </VStack>
          </CardHeader>
        </Card>

        {/* Prize Showcase */}
        <PrizeShowcase />

        {/* Sticky Sign In Call to Action */}
        <Box
          position="sticky"
          top={0}
          zIndex={10}
          mb={6}
        >
          <Card bg="blue.900" borderColor="blue.500" borderWidth={2}>
            <CardBody textAlign="center" py={6}>
              <VStack spacing={3}>
                <Heading size="md" color="blue.100">
                  🗳️ Join the Action!
                </Heading>
                <Text color="blue.200" fontSize="md" maxW="md">
                  Sign in to vote on fight winners, see live results, and compete with other fans!
                </Text>
                <Button
                  colorScheme="blue"
                  size="lg"
                  onClick={handleSignIn}
                  px={8}
                >
                  Sign In to Vote
                </Button>
                <Text fontSize="sm" color="blue.300">
                  Free to join • Vote in real-time • See live statistics
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </Box>

        {/* Fight Preview (Read-only) */}
        <VStack spacing={0} align="stretch">
          {event?.fights?.map((fight, index) => (
            <Card key={fight.id} opacity={0.8}>
              <CardHeader pb={{ base: 2, md: 4 }}>
                <VStack align="start" spacing={2} w="full">
                  <Heading size={{ base: "sm", md: "md" }} lineHeight="short">
                    Fight {index + 1}: {fight?.fighter1?.name || 'Fighter 1'} vs {fight?.fighter2?.name || 'Fighter 2'}
                  </Heading>
                  <VStack align="start" spacing={1} w="full">
                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                      {fight?.weightClass || 'Weight Class TBD'} • {fight?.rounds || 3} rounds
                    </Text>
                    <HStack spacing={2} flexWrap="wrap">
                      <Badge colorScheme={getStatusColor(fight?.status)} fontSize="xs">
                        {fight?.status || 'upcoming'}
                      </Badge>
                      {fight?.status === 'active' && fight?.currentRound && (
                        <Badge colorScheme="orange" fontSize="xs">
                          Round {fight.currentRound}
                        </Badge>
                      )}
                    </HStack>
                  </VStack>
                </VStack>
              </CardHeader>
              <CardBody>
                <HStack spacing={{ base: 3, md: 6 }} align="start" justify="center" pt={4} pb={0}>
                  {/* Fighter 1 */}
                  <VStack spacing={3} flex={1} maxW={{ base: "140px", md: "200px" }}>
                    <Box
                      position="relative"
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="lg"
                      border="3px solid"
                      borderColor="black"
                    >
                      <Image
                        src={fight?.fighter1?.photoUrl}
                        alt={fight?.fighter1?.name}
                        width={{ base: "120px", md: "150px" }}
                        height={{ base: "120px", md: "150px" }}
                        objectFit="cover"
                        bg="white"
                        fallback={
                          <Box
                            width={{ base: "120px", md: "150px" }}
                            height={{ base: "120px", md: "150px" }}
                            bg="gray.700"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="xs" color="gray.400" textAlign="center">
                              No Photo
                            </Text>
                          </Box>
                        }
                      />
                    </Box>
                    <VStack spacing={1} minH="60px" justify="start">
                      <Text fontSize={{ base: "sm", md: "md" }} fontWeight="bold" color="white" textAlign="center" lineHeight="short">
                        {fight?.fighter1?.name}
                      </Text>
                      {fight?.fighter1?.nickname && (
                        <Text fontSize="xs" color="gray.400" fontStyle="italic" textAlign="center">
                          "{fight.fighter1.nickname}"
                        </Text>
                      )}
                      <HStack spacing={2}>
                        {fight?.fighter1?.record && (
                          <Badge variant="outline" colorScheme="gray" fontSize="xs">
                            {fight.fighter1.record}
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </VStack>

                  {/* VS Display */}
                  <Box
                    bg="red.600"
                    color="white"
                    px={{ base: 3, md: 4 }}
                    py={{ base: 1, md: 2 }}
                    mt={10}
                    borderRadius="full"
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="bold"
                    boxShadow="lg"
                    border="2px solid"
                    borderColor="red.400"
                    textShadow="1px 1px 2px rgba(0,0,0,0.5)"
                  >
                    VS
                  </Box>

                  {/* Fighter 2 */}
                  <VStack spacing={3} flex={1} maxW={{ base: "140px", md: "200px" }}>
                    <Box
                      position="relative"
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="lg"
                      border="3px solid"
                      borderColor="black"
                    >
                      <Image
                        src={fight?.fighter2?.photoUrl}
                        alt={fight?.fighter2?.name}
                        width={{ base: "120px", md: "150px" }}
                        height={{ base: "120px", md: "150px" }}
                        objectFit="cover"
                        bg="white"
                        fallback={
                          <Box
                            width={{ base: "120px", md: "150px" }}
                            height={{ base: "120px", md: "150px" }}
                            bg="gray.700"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="xs" color="gray.400" textAlign="center">
                              No Photo
                            </Text>
                          </Box>
                        }
                      />
                    </Box>
                    <VStack spacing={1} minH="60px" justify="start">
                      <Text fontSize={{ base: "sm", md: "md" }} fontWeight="bold" color="white" textAlign="center" lineHeight="short">
                        {fight?.fighter2?.name}
                      </Text>
                      {fight?.fighter2?.nickname && (
                        <Text fontSize="xs" color="gray.400" fontStyle="italic" textAlign="center">
                          "{fight.fighter2.nickname}"
                        </Text>
                      )}
                      <HStack spacing={2}>
                        {fight?.fighter2?.record && (
                          <Badge variant="outline" colorScheme="gray" fontSize="xs">
                            {fight.fighter2.record}
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </VStack>
                </HStack>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontWeight="bold" mb={3} fontSize={{ base: "sm", md: "md" }}>
                      Who will win the fight?
                    </Text>
                    <Stack direction={{ base: "column", md: "row" }} spacing={3} mb={4} w="full">
                      <Button
                        size={{ base: "md", md: "lg" }}
                        w="full"
                        variant="outline"
                        colorScheme="gray"
                        isDisabled
                        fontSize={{ base: "sm", md: "md" }}
                        opacity={0.6}
                      >
                        {fight?.fighter1?.name || 'Fighter 1'}
                      </Button>
                      <Button
                        size={{ base: "md", md: "lg" }}
                        w="full"
                        variant="outline"
                        colorScheme="gray"
                        isDisabled
                        fontSize={{ base: "sm", md: "md" }}
                        opacity={0.6}
                      >
                        {fight?.fighter2?.name || 'Fighter 2'}
                      </Button>
                    </Stack>

                    {/* Always show sign-in prompt - no results for anonymous users */}
                    <Box
                      p={4}
                      bg="gray.800"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.600"
                      textAlign="center"
                    >
                      <Text fontSize="sm" color="gray.400" fontStyle="italic">
                        🔒 Sign in to see {fight?.winnerId ? 'fight results' : 'live vote results'} and cast your vote
                      </Text>
                    </Box>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          )) || []}

          {(!event?.fights || event.fights.length === 0) && (
            <Card>
              <CardBody textAlign="center" py={10}>
                <Text color="gray.400">
                  No fights scheduled for this event yet.
                </Text>
              </CardBody>
            </Card>
          )}
        </VStack>

      </Box>
    );
  }

  return (
    <Box maxW="4xl" mx="auto" p={{ base: 2, md: 4 }}>
      <Card mb={6}>
        {/* Cover Image */}
        {event?.coverImage && (
          <Box>
            <Image
              src={event.coverImage}
              alt={`${event.name} cover image`}
              width="100%"
              height={{ base: "200px", md: "300px" }}
              objectFit="cover"
              borderTopRadius="lg"
            />
          </Box>
        )}
        <CardHeader pb={{ base: 3, md: 6 }}>
          <VStack align="start" spacing={2}>
            <Heading size={{ base: "md", md: "lg" }}>{event?.name || 'Event'}</Heading>
            <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>
              {event?.location || 'Location TBD'}
            </Text>
            <Text color="gray.400" fontSize={{ base: "xs", md: "sm" }}>
              {event?.date instanceof Date
                ? event.date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })
                : 'Date TBD'
              }
            </Text>
            {event?.description && (
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                {event.description}
              </Text>
            )}
          </VStack>
        </CardHeader>
      </Card>

      {/* Prize Showcase */}
      <PrizeShowcase />

      <VStack spacing={6} align="stretch">
        {event?.fights?.map((fight, index) => (
          <Card key={fight.id}>
            <CardHeader pb={{ base: 2, md: 4 }}>
              <VStack align="start" spacing={2} w="full">
                <Heading size={{ base: "sm", md: "md" }} lineHeight="short">
                  Fight {index + 1}: {fight?.fighter1?.name || 'Fighter 1'} vs {fight?.fighter2?.name || 'Fighter 2'}
                </Heading>
                <VStack align="start" spacing={1} w="full">
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                    {fight?.weightClass || 'Weight Class TBD'} • {fight?.rounds || 3} rounds
                  </Text>
                  <HStack spacing={2} flexWrap="wrap">
                    <Badge colorScheme={getStatusColor(fight?.status)} fontSize="xs">
                      {fight?.status || 'upcoming'}
                    </Badge>
                    {fight?.status === 'active' && fight?.currentRound && (
                      <Badge colorScheme="orange" fontSize="xs">
                        Round {fight.currentRound}
                      </Badge>
                    )}
                  </HStack>
                </VStack>
              </VStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={0} align="stretch">
                {/* Fighter Photos Display */}
                <HStack spacing={{ base: 3, md: 6 }} align="start" justify="center" pt={4} pb={0}>
                  {/* Fighter 1 */}
                  <VStack spacing={3} flex={1} maxW={{ base: "140px", md: "200px" }}>
                    <Box
                      position="relative"
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="lg"
                      border="3px solid"
                      borderColor={
                        fight?.winnerId
                          ? (fight?.winnerId === fight?.fighter1?.id ? "gold" : "gray.500")
                          : (userVotes[`${fight?.id}_fight`] === fight?.fighter1?.id ? "green.500" : "gray.500")
                      }
                    >
                      <Image
                        src={fight?.fighter1?.photoUrl}
                        alt={fight?.fighter1?.name}
                        width={{ base: "120px", md: "150px" }}
                        height={{ base: "120px", md: "150px" }}
                        objectFit="cover"
                        bg="white"
                        fallback={
                          <Box
                            width={{ base: "120px", md: "150px" }}
                            height={{ base: "120px", md: "150px" }}
                            bg="gray.700"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="xs" color="gray.400" textAlign="center">
                              No Photo
                            </Text>
                          </Box>
                        }
                      />
                      {/* Show vote indicator only if fight is not completed, or winner indicator if completed */}
                      {!fight?.winnerId && userVotes[`${fight?.id}_fight`] === fight?.fighter1?.id && (
                        <Box
                          position="absolute"
                          top={0}
                          right={0}
                          bg="blue.500"
                          color="white"
                          borderRadius="full"
                          width="40px"
                          height="40px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="2xl"
                          border="3px solid white"
                          boxShadow="lg"
                        >
                          🗳️
                        </Box>
                      )}
                      {fight?.winnerId && fight?.winnerId === fight?.fighter1?.id && (
                        <Box
                          position="absolute"
                          top={0}
                          right={0}
                          bg="gold"
                          color="black"
                          borderRadius="full"
                          width="40px"
                          height="40px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="xl"
                          fontWeight="bold"
                          border="3px solid white"
                          boxShadow="lg"
                        >
                          🏆
                        </Box>
                      )}
                    </Box>
                    <VStack spacing={1} minH="60px" justify="start">
                      <Text fontSize={{ base: "sm", md: "md" }} fontWeight="bold" color="white" textAlign="center" lineHeight="short">
                        {fight?.fighter1?.name}
                      </Text>
                      {fight?.fighter1?.nickname && (
                        <Text fontSize="xs" color="gray.400" fontStyle="italic" textAlign="center">
                          "{fight.fighter1.nickname}"
                        </Text>
                      )}
                      <HStack spacing={2}>
                        {fight?.fighter1?.record && (
                          <Badge variant="outline" colorScheme="gray" fontSize="xs">
                            {fight.fighter1.record}
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </VStack>

                  {/* VS Display */}
                  <Box
                    bg="red.600"
                    color="white"
                    px={{ base: 3, md: 4 }}
                    py={{ base: 1, md: 2 }}
                    mt={10}
                    borderRadius="full"
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="bold"
                    boxShadow="lg"
                    border="2px solid"
                    borderColor="red.400"
                    textShadow="1px 1px 2px rgba(0,0,0,0.5)"
                  >
                    VS
                  </Box>

                  {/* Fighter 2 */}
                  <VStack spacing={3} flex={1} maxW={{ base: "140px", md: "200px" }}>
                    <Box
                      position="relative"
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="lg"
                      border="3px solid"
                      borderColor={
                        fight?.winnerId
                          ? (fight?.winnerId === fight?.fighter2?.id ? "gold" : "gray.500")
                          : (userVotes[`${fight?.id}_fight`] === fight?.fighter2?.id ? "green.500" : "gray.500")
                      }
                    >
                      <Image
                        src={fight?.fighter2?.photoUrl}
                        alt={fight?.fighter2?.name}
                        width={{ base: "120px", md: "150px" }}
                        height={{ base: "120px", md: "150px" }}
                        objectFit="cover"
                        bg="white"
                        fallback={
                          <Box
                            width={{ base: "120px", md: "150px" }}
                            height={{ base: "120px", md: "150px" }}
                            bg="gray.700"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="xs" color="gray.400" textAlign="center">
                              No Photo
                            </Text>
                          </Box>
                        }
                      />
                      {/* Show vote indicator only if fight is not completed, or winner indicator if completed */}
                      {!fight?.winnerId && userVotes[`${fight?.id}_fight`] === fight?.fighter2?.id && (
                        <Box
                          position="absolute"
                          top={0}
                          right={0}
                          bg="blue.500"
                          color="white"
                          borderRadius="full"
                          width="40px"
                          height="40px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="2xl"
                          border="3px solid white"
                          boxShadow="lg"
                        >
                          🗳️
                        </Box>
                      )}
                      {fight?.winnerId && fight?.winnerId === fight?.fighter2?.id && (
                        <Box
                          position="absolute"
                          top={0}
                          right={0}
                          bg="gold"
                          color="black"
                          borderRadius="full"
                          width="40px"
                          height="40px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="xl"
                          fontWeight="bold"
                          border="3px solid white"
                          boxShadow="lg"
                        >
                          🏆
                        </Box>
                      )}
                    </Box>
                    <VStack spacing={1} minH="60px" justify="start">
                      <Text fontSize={{ base: "sm", md: "md" }} fontWeight="bold" color="white" textAlign="center" lineHeight="short">
                        {fight?.fighter2?.name}
                      </Text>
                      {fight?.fighter2?.nickname && (
                        <Text fontSize="xs" color="gray.400" fontStyle="italic" textAlign="center">
                          "{fight.fighter2.nickname}"
                        </Text>
                      )}
                      <HStack spacing={2}>
                        {fight?.fighter2?.record && (
                          <Badge variant="outline" colorScheme="gray" fontSize="xs">
                            {fight.fighter2.record}
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </VStack>
                </HStack>

                {/* Fight Winner Voting */}
                <Box>
                  <Text fontWeight="bold" mb={3} fontSize={{ base: "sm", md: "md" }}>
                    Who will win the fight?
                  </Text>
                  <Stack direction={{ base: "column", md: "row" }} spacing={3} mb={4} w="full">
                    <Button
                      size={{ base: "md", md: "lg" }}
                      w="full"
                      colorScheme={userVotes[`${fight?.id}_fight`] === fight?.fighter1?.id ? "green" : "blue"}
                      variant={userVotes[`${fight?.id}_fight`] === fight?.fighter1?.id ? "solid" : "outline"}
                      onClick={() => handleVoteClick(fight, fight?.fighter1?.id, 'fight_winner')}
                      isDisabled={fight?.winnerId || !fight?.fighter1?.id || userVotes[`${fight?.id}_fight`] || isSubmittingVote}
                      isLoading={isSubmittingVote}
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      {fight?.fighter1?.name || 'Fighter 1'}
                      {userVotes[`${fight?.id}_fight`] === fight?.fighter1?.id && " 🗳️"}
                    </Button>
                    <Button
                      size={{ base: "md", md: "lg" }}
                      w="full"
                      colorScheme={userVotes[`${fight?.id}_fight`] === fight?.fighter2?.id ? "green" : "blue"}
                      variant={userVotes[`${fight?.id}_fight`] === fight?.fighter2?.id ? "solid" : "outline"}
                      onClick={() => handleVoteClick(fight, fight?.fighter2?.id, 'fight_winner')}
                      isDisabled={fight?.winnerId || !fight?.fighter2?.id || userVotes[`${fight?.id}_fight`] || isSubmittingVote}
                      isLoading={isSubmittingVote}
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      {fight?.fighter2?.name || 'Fighter 2'}
                      {userVotes[`${fight?.id}_fight`] === fight?.fighter2?.id && " 🗳️"}
                    </Button>
                  </Stack>

                  {/* Show winner and vote accuracy for fights with results */}
                  {fight?.winnerId && (
                    <VStack spacing={3} mb={4}>
                      {/* Fight Result */}
                      <Box
                        p={3}
                        bg="green.900"
                        borderRadius="md"
                        border="2px solid"
                        borderColor="green.500"
                        textAlign="center"
                        w="full"
                      >
                        <Text fontSize="sm" color="green.200" mb={1}>
                          Fight Result
                        </Text>
                        <Text fontWeight="bold" color="green.100" fontSize="lg">
                          {fight.winnerId === 'draw' ? 'Draw' :
                            fight.winnerId === 'no-contest' ? 'No Contest' :
                              fight.winnerId === fight.fighter1?.id ? fight.fighter1?.name :
                                fight.winnerId === fight.fighter2?.id ? fight.fighter2?.name :
                                  'Winner'}
                          {fight.winnerId !== 'draw' && fight.winnerId !== 'no-contest' && ' Wins!'}
                        </Text>
                      </Box>

                      {/* Vote Accuracy Feedback */}
                      {(() => {
                        const userVotedFor = userVotes[`${fight?.id}_fight`];
                        const didVote = !!userVotedFor;
                        const votedCorrectly = didVote && userVotedFor === fight.winnerId;

                        if (!didVote) {
                          return (
                            <Box
                              p={3}
                              bg="gray.700"
                              borderRadius="md"
                              border="2px solid"
                              borderColor="gray.500"
                              textAlign="center"
                              w="full"
                            >
                              <Text fontSize="sm" color="gray.300" mb={1}>
                                Your Vote
                              </Text>
                              <Text fontWeight="bold" color="gray.200">
                                You didn't vote on this fight
                              </Text>
                            </Box>
                          );
                        }

                        if (votedCorrectly) {
                          return (
                            <Box
                              p={3}
                              bg="green.800"
                              borderRadius="md"
                              border="2px solid"
                              borderColor="green.400"
                              textAlign="center"
                              w="full"
                            >
                              <Text fontSize="sm" color="green.200" mb={1}>
                                Your Vote
                              </Text>
                              <Text fontWeight="bold" color="green.100">
                                You voted correctly! 🎉
                              </Text>
                            </Box>
                          );
                        }

                        // Voted incorrectly
                        const votedForName = userVotedFor === fight.fighter1?.id ? fight.fighter1?.name :
                          userVotedFor === fight.fighter2?.id ? fight.fighter2?.name :
                            userVotedFor === 'draw' ? 'Draw' :
                              userVotedFor === 'no-contest' ? 'No Contest' : 'Unknown';

                        return (
                          <Box
                            p={3}
                            bg="red.800"
                            borderRadius="md"
                            border="2px solid"
                            borderColor="red.400"
                            textAlign="center"
                            w="full"
                          >
                            <Text fontSize="sm" color="red.200" mb={1}>
                              Your Vote
                            </Text>
                            <Text fontWeight="bold" color="red.100">
                              You voted for {votedForName}
                            </Text>
                            <Text fontSize="sm" color="red.200" mt={1}>
                              Better luck next time! 💪
                            </Text>
                          </Box>
                        );
                      })()}
                    </VStack>
                  )}

                  {renderVoteStats(voteStats[`${fight?.id}_fight`] || [], `${fight?.id}_fight`, fight)}
                </Box>

                {/* Round Winner Voting - Hidden for MVP, keeping functionality for future */}
                {/* TODO: Re-enable round voting in future version */}
                {false && fight?.status === 'active' && fight?.currentRound && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" mb={3} fontSize={{ base: "sm", md: "md" }}>
                        Who is winning Round {fight?.currentRound}?
                      </Text>
                      <VStack spacing={3} mb={4} w="full">
                        <Button
                          size={{ base: "md", md: "sm" }}
                          w="full"
                          colorScheme={userVotes[`${fight?.id}_round_${fight?.currentRound}`] === fight?.fighter1?.id ? "green" : "orange"}
                          variant={userVotes[`${fight?.id}_round_${fight?.currentRound}`] === fight?.fighter1?.id ? "solid" : "outline"}
                          onClick={() => handleVoteClick(fight, fight?.fighter1?.id, 'round_winner', fight?.currentRound)}
                          isDisabled={!fight?.fighter1?.id || userVotes[`${fight?.id}_round_${fight?.currentRound}`] || isSubmittingVote}
                          isLoading={isSubmittingVote}
                          fontSize={{ base: "sm", md: "md" }}
                        >
                          {fight?.fighter1?.name || 'Fighter 1'}
                          {userVotes[`${fight?.id}_round_${fight?.currentRound}`] === fight?.fighter1?.id && " ✓"}
                        </Button>
                        <Button
                          size={{ base: "md", md: "sm" }}
                          w="full"
                          colorScheme={userVotes[`${fight?.id}_round_${fight?.currentRound}`] === fight?.fighter2?.id ? "green" : "orange"}
                          variant={userVotes[`${fight?.id}_round_${fight?.currentRound}`] === fight?.fighter2?.id ? "solid" : "outline"}
                          onClick={() => handleVoteClick(fight, fight?.fighter2?.id, 'round_winner', fight?.currentRound)}
                          isDisabled={!fight?.fighter2?.id || userVotes[`${fight?.id}_round_${fight?.currentRound}`] || isSubmittingVote}
                          isLoading={isSubmittingVote}
                          fontSize={{ base: "sm", md: "md" }}
                        >
                          {fight?.fighter2?.name || 'Fighter 2'}
                          {userVotes[`${fight?.id}_round_${fight?.currentRound}`] === fight?.fighter2?.id && " ✓"}
                        </Button>
                      </VStack>
                      {renderVoteStats(voteStats[`${fight?.id}_round_${fight?.currentRound}`] || [], `${fight?.id}_round_${fight?.currentRound}`, fight)}
                    </Box>
                  </>
                )}
              </VStack>
            </CardBody>
          </Card>
        )) || []}

        {(!event?.fights || event.fights.length === 0) && (
          <Card>
            <CardBody textAlign="center" py={10}>
              <Text color="gray.400">
                No fights scheduled for this event yet.
              </Text>
            </CardBody>
          </Card>
        )}

        {/* Vote Score Summary */}
        {event?.fights && event.fights.length > 0 && (() => {
          const completedFights = event.fights.filter(fight => fight.winnerId);
          const votedFights = completedFights.filter(fight => userVotes[`${fight.id}_fight`]);
          const correctVotes = completedFights.filter(fight =>
            userVotes[`${fight.id}_fight`] === fight.winnerId
          ).length;

          if (completedFights.length === 0) return null;

          return (
            <Card mt={6}>
              <CardBody textAlign="center">
                <VStack spacing={3}>
                  <Text fontSize="lg" fontWeight="bold" color="blue.400">
                    Your Vote Summary
                  </Text>
                  <HStack spacing={6} justify="center">
                    <VStack spacing={1}>
                      <Text fontSize="2xl" fontWeight="bold" color="green.400">
                        {correctVotes}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        Correct
                      </Text>
                    </VStack>
                    <VStack spacing={1}>
                      <Text fontSize="2xl" fontWeight="bold" color="red.400">
                        {votedFights.length - correctVotes}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        Incorrect
                      </Text>
                    </VStack>
                    <VStack spacing={1}>
                      <Text fontSize="2xl" fontWeight="bold" color="gray.400">
                        {completedFights.length - votedFights.length}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        Missed
                      </Text>
                    </VStack>
                  </HStack>
                  <Text fontSize="sm" color="gray.500">
                    {votedFights.length > 0 ?
                      `${Math.round((correctVotes / votedFights.length) * 100)}% accuracy on votes cast` :
                      'No votes cast yet'
                    }
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          );
        })()}
      </VStack>

      {/* Vote Confirmation Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        returnFocusOnClose={false}
        blockScrollOnMount={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Your Vote</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Are you sure you want to vote for{' '}
                <Text as="span" fontWeight="bold" color="blue.400">
                  {pendingVote?.fighterName}
                </Text>{' '}
                as the{' '}
                <Text as="span" fontWeight="bold">
                  {pendingVote?.voteTypeText}
                </Text>
                ?
              </Text>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Once you vote, you cannot change your selection for this fight/round.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={confirmVote}>
              Confirm Vote
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
